import { Transition, Dialog } from '@headlessui/react'
import { classNames } from '@progress/kendo-react-common'
import CustomSwitch from 'components/appointmentDrawer/CustomSwitch'
import GenericError from 'components/GenericError/GenericError'
import Translation from 'components/Translation'
import { useTenantContext } from 'context/TenantContext'
import React, { Fragment, useEffect, useState } from 'react'
import { SetStateAction } from 'react'
import { Dispatch } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import {
    DEFAULT_ERROR_STATE,
    IError,
    useAppContext,
} from '../../context/AppContext'
import { SpaceForm } from '../../types/spaces'
import { getTranslation } from '../../utils/language-helper'
import ImageFileUploader from '../FileUploader/Image'
import { ModalDataAttributes, SubmitError } from '../Modals/types'
import StepperInput from '../Stepper'

export interface SpaceProps {
    openModal: boolean
    setOpenModal: Dispatch<SetStateAction<boolean>>
    space_data: SpaceForm
    onSaveHandler: (space: SpaceForm) => void
    shouldDelete?: boolean
    translation?: Record<string, string>
    refetchData?: any
}

export type SpaceFormValues = {
    name: string
    max_capacity: number
}

function AddSpace({
    openModal,
    setOpenModal,
    space_data,
    onSaveHandler,
    translation,
}: SpaceProps) {
    const { tenant } = useTenantContext()
    const { translations } = useAppContext()
    const [attributes, setAttributes] =
        useState<ModalDataAttributes>(space_data)
    const [is_dirty, setIsDirty] = useState(false)
    const [api_error, setApiError] = useState<IError>(DEFAULT_ERROR_STATE)
    const [error_message, setErrorMessage] = useState<string>('')
    const [error_messages, setErrorMessages] = useState<string[]>([])
    const [show_upload_view, setShowUploadView] = useState(true)
    const [allow_reuse, setAllowReuse] = useState(false)

    useEffect(() => {
        setAllowReuse(space_data?.reuse as boolean)
    }, [space_data])

    const { handleSubmit, reset } = useForm<SpaceFormValues>({
        mode: 'onSubmit',
    })
    const [location_space_img, seLocationSpaceImg] = useState(
        (attributes?.image_url as string) || ''
    )

    const onSubmit: SubmitHandler<SpaceFormValues> = async () => {
        const payload: SpaceForm = {
            id: attributes.id as string,
            location_id: attributes.location_id as string,
            max_capacity: attributes.max_capacity as number,
            name: attributes.name as string,
            notes: attributes.notes as string,
            image_url: location_space_img as string,
            reuse: allow_reuse,
        }
        if (!payload?.name || !payload?.max_capacity) {
            setErrorMessage('space_required_fields')
        } else {
            onSaveHandler(payload)
        }
    }

    function UrlPreviewComponent(props: any) {
        return (
            <div className="grid gap-2 justify-center">
                <img
                    src={props.image_url}
                    className="w-[28rem] h-36 rounded-lg object-cover"
                    alt={props.image_alt}
                />
                <i
                    className={classNames(
                        'feather-x-circle text-cyan-900',
                        'absolute right-[3rem] text-xl cursor-pointer mt-1'
                    )}
                    onClick={() => {
                        handleRemove()
                    }}
                />
                <div className="w-full">
                    <button
                        onClick={() => {
                            handleRemove()
                        }}
                        className="flex gap-3 items-center bg-primary-light text-white rounded-lg justify-center mb-3 py-2 w-full"
                    >
                        <i className="feather-image text-xl" />
                        <Translation
                            render_as="span"
                            content_key="replace_photo"
                        />
                    </button>
                </div>
            </div>
        )
    }

    function handleOnAdd(image_url: string) {
        seLocationSpaceImg(image_url)
        setShowUploadView(false)
        setIsDirty(true)
    }

    function handleRemove() {
        seLocationSpaceImg('')
        setShowUploadView(true)
        setIsDirty(true)
    }

    function showUploadView() {
        if (!show_upload_view || location_space_img !== '') {
            return (
                <UrlPreviewComponent
                    image_url={location_space_img as string}
                    image_alt={
                        translations['location_space_image'] ||
                        'Location Space Image'
                    }
                />
            )
        } else {
            return (
                <ImageFileUploader
                    tenant_id={tenant?.id}
                    onAdd={handleOnAdd}
                    accept=".jpeg, .jpg, .gif, .png"
                    form_context={{
                        attributes: attributes || {},
                        setAttributes: setAttributes as unknown as (
                            data: Record<string, string | File>
                        ) => void,
                    }}
                    addClassNames={{
                        dropzone:
                            'bg-transparent p-0 overflow-hidden h-64 border-0',
                        inputLabel:
                            'text-gray-400 font-medium text-lg flex-grow',
                        submitButtonContainer: 'mt-3',
                        submitButton:
                            'flex gap-3 items-center bg-primary-light text-white px-6 py-3 rounded-lg w-full text-lg font-serif font-light justify-center',
                    }}
                />
            )
        }
    }

    return (
        <Transition
            appear
            show={openModal}
            as="div"
            enter="transition-opacity duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <Dialog
                as="div"
                className="fixed inset-0 z-[101] overflow-y-auto"
                onClose={() => setOpenModal(false)}
            >
                <div className="min-h-screen px-4 text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 backdrop-filter backdrop-brightness-50" />
                    </Transition.Child>

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span
                        className="inline-block my-8 align-middle"
                        aria-hidden="true"
                    >
                        &#8203;
                    </span>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="inline-block min-w-[50%] w-auto p-10 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                                {api_error.message?.length > 1 && (
                                    <GenericError
                                        error_message={api_error?.message}
                                        onCloseModal={() => {
                                            setApiError(DEFAULT_ERROR_STATE)
                                            setOpenModal(false)
                                        }}
                                        tag={api_error?.tag}
                                    />
                                )}
                                <div className="grid mb-7 justify-center">
                                    <Translation
                                        render_as="div"
                                        className="text-xl text-black font-semibold text-center"
                                        content_key="add_space"
                                        translations={translation}
                                    />
                                    <Translation
                                        render_as="div"
                                        className="text-sm text-center"
                                        style={{ color: '#23476B' }}
                                        content_key="create_new_space"
                                        translations={translation}
                                    />
                                </div>
                                {error_message ? (
                                    <div className="bg-red-200 text-red-700 bg-opacity-30 p-4 mt-3 mb-6 rounded-lg">
                                        <Translation
                                            content_key={error_message}
                                        />
                                    </div>
                                ) : (
                                    ''
                                )}
                                {error_messages.length ? (
                                    <div className="bg-red-200 text-red-700 bg-opacity-30 p-4 mt-3 mb-6 rounded-lg">
                                        {error_messages.map(
                                            (msg: string, idx: number) => (
                                                <div key={idx}>
                                                    {
                                                        <Translation
                                                            content_key={
                                                                msg
                                                            }
                                                        />
                                                    }
                                                </div>
                                            )
                                        )}
                                    </div>
                                ) : (
                                    ''
                                )}
                                <div className="flex gap-10 w-full">
                                    <div className="w-1/2 mb-5">
                                        <label
                                            htmlFor="space-name"
                                            className="text-sm mb-1"
                                        >
                                            <Translation content_key="space_name" />
                                        </label>
                                        <sup className="text-red-700">
                                            *
                                        </sup>
                                        <input
                                            type="text"
                                            id="space-name"
                                            autoComplete="name"
                                            className={classNames(
                                                'px-6 py-3 mt-1 mb-3 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md'
                                            )}
                                            defaultValue={
                                                (space_data?.name as string) ||
                                                ''
                                            }
                                            onChange={(e) => {
                                                setIsDirty(false)
                                                if (e.target.value) {
                                                    setIsDirty(true)
                                                    setAttributes({
                                                        ...attributes,
                                                        name: e.target
                                                            .value,
                                                    })
                                                }
                                            }}
                                        />
                                        <label
                                            htmlFor="max-capacity"
                                            className="text-sm"
                                        >
                                            <Translation content_key="space_maximum_capacity" />
                                            <sup className="text-red-700">
                                                *
                                            </sup>
                                        </label>
                                        <div className="mb-3">
                                            <StepperInput
                                                default_value={
                                                    (space_data?.max_capacity as number) ||
                                                    0
                                                }
                                                disabled={false}
                                                max_value={999}
                                                onChange={(v: number) => {
                                                    if (v) {
                                                        setIsDirty(true)
                                                        setAttributes({
                                                            ...attributes,
                                                            max_capacity: v,
                                                        })
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <CustomSwitch
                                                value={allow_reuse}
                                                onChangeValue={(value) => {
                                                    setIsDirty(true)
                                                    setAllowReuse(value)
                                                    setAttributes({
                                                        ...attributes,
                                                        reuse: value,
                                                    })
                                                }}
                                                label={
                                                    <div className="flex items-center">
                                                        {' '}
                                                        <Translation content_key="allow_reuse" />{' '}
                                                        <span
                                                            title={
                                                                translations[
                                                                    'allow_sharing_location_space_tip'
                                                                ] ||
                                                                'allow_sharing_location_space_tip'
                                                            }
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-5 w-5 mr-1 ml-2 mt-0 text-gray-400 cursor-help"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                />
                                                            </svg>
                                                        </span>
                                                    </div>
                                                }
                                                className="mb-5 mr-2"
                                                labelStyles={{
                                                    fontSize: '14px',
                                                }}
                                            />
                                        </div>
                                        <label
                                            htmlFor="space-notes"
                                            className="text-sm"
                                        >
                                            <Translation content_key="Notes" />
                                        </label>
                                        <textarea
                                            id="notes"
                                            className={classNames(
                                                'placeholder-gray-300 mt-1 h-40 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm border-gray-300 rounded-md px-6 py-3'
                                            )}
                                            placeholder={getTranslation(
                                                'locations.description_label',
                                                translations
                                            )}
                                            defaultValue={
                                                space_data?.notes as string
                                            }
                                            onChange={(e) => {
                                                setIsDirty(true)
                                                setAttributes({
                                                    ...attributes,
                                                    notes: e.target.value,
                                                })
                                            }}
                                        />
                                    </div>
                                    <div className="w-1/2 mb-5">
                                        <label
                                            htmlFor="space-photo"
                                            className="text-sm mb-1"
                                        >
                                            <Translation content_key="locations.photo" />
                                            <sup className="text-red-700">
                                                *
                                            </sup>
                                        </label>
                                        {showUploadView()}
                                    </div>
                                </div>

                                <div className="flex justify-end">
                                    <div className="flex justify-start pl-4 pb-4">
                                        <button
                                            type="button"
                                            className="border border-gray-300 rounded-lg py-3 inline-block mr-3 px-10"
                                            onClick={(e) => {
                                                setOpenModal(false)
                                            }}
                                        >
                                            <Translation content_key="cancel" />
                                        </button>
                                        <button
                                            disabled={!is_dirty}
                                            type="submit"
                                            className={classNames(
                                                'group relative flex justify-center',
                                                'py-3 px-12 border border-transparent',
                                                'rounded-md text-white',
                                                'focus:outline-none',
                                                is_dirty
                                                    ? 'bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                                    : 'bg-gray-400 cursor-not-allowed'
                                            )}
                                        >
                                            <Translation
                                                content_key={
                                                    !attributes.id
                                                        ? 'locations.add_space'
                                                        : 'save'
                                                }
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    )
}

export default AddSpace
